import React, {ReactNode, useState} from 'react'
import moment from 'moment'
import _ from 'underscore'

import {P} from '../UI/index.js'
import {buttonResetStyle} from '../UI/buttons/styles.js'
import {focusableClass, ensureFocusStyles} from '../UI/styles.js'
import type {ShiftbaseSyncRoster, ShiftbaseSyncSummary} from '../../../server/functions/shifts/shifts.types.js'

ensureFocusStyles()

const count = (amount: number, singular: string, plural: string) => `${amount} ${amount === 1 ? singular : plural}`

/** Why a roster was skipped: in the title of the warning, and as the heading of its shifts */
const skippedReasons: Record<ShiftbaseSyncSummary['skipped'][number]['reason'], {title: string, heading: string}> = {
    notLinked: {title: 'van medewerkers die niet aan een gebruiker gekoppeld zijn', heading: 'Medewerker niet aan een gebruiker gekoppeld'},
    noMatch: {title: 'zonder passende dienst', heading: 'Geen passende dienst'},
    removed: {title: 'van een verwijderde dienst', heading: 'Dienst verwijderd'}
}

/** The shift a roster concerns: date, times, name and the courier (or the Shiftbase employee when not linked) */
function rosterLabel(roster: ShiftbaseSyncRoster) {
    const times = roster.startTime || roster.endTime ? ` ${roster.startTime}–${roster.endTime}` : ''
    const employee = roster.messengerName || `Shiftbase medewerker ${roster.shiftbaseUserId}`

    return [`${moment(roster.date).format('DD-MM-YYYY')}${times}`, roster.name, employee].filter(Boolean).join(' · ')
}

type RosterGroup = {heading: string, rosters: ShiftbaseSyncRoster[]}

/** The content of an alert: one title that expands to the shifts it concerns, under a heading per group when there are more */
function ExpandableAlertContent({title, groups}: {title: string, groups: RosterGroup[]}) {
    const [expanded, setExpanded] = useState(false)

    return (
        <div>
            <button
                type='button'
                className={focusableClass}
                style={{...buttonResetStyle, display: 'flex', alignItems: 'center', textAlign: 'left', cursor: 'pointer'}}
                aria-expanded={expanded}
                onClick={() => setExpanded(!expanded)}
            >
                <P>{title}</P>
                <i aria-hidden style={{marginLeft: 6}} className={expanded ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'} />
            </button>

            {expanded &&
                <div style={{marginTop: 8, maxHeight: 240, overflowY: 'auto'}}>
                    {groups.map(({heading, rosters}) => (
                        <div key={heading} style={{marginBottom: 8}}>
                            {groups.length > 1 && <P style={{fontWeight: 500}}>{heading}</P>}

                            <ul style={{margin: 0, paddingLeft: 20}}>
                                {_.sortBy(rosters, (roster) => `${roster.date} ${roster.startTime}`).map((roster) => (
                                    <li key={roster.rosterId}><P>{rosterLabel(roster)}</P></li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            }
        </div>
    )
}

/**
 * What the Shiftbase sync did, for the popup: the rosters it got and the shifts it updated or created as the message,
 * the skipped rosters as the warning and the failed rosters as the error, so at most one alert of each. The warning
 * and the error each have one title that expands to the shifts they concern, grouped by reason or error. Errors
 * without a roster (Shiftbase could not be reached) are shown below each other in the error.
 */
export function shiftbaseSyncResult(summary: ShiftbaseSyncSummary | undefined, errors: string[] = []): {message: ReactNode, warning: ReactNode, error: ReactNode} {
    const plainError = errors.join('\n')

    if (!summary) {
        return {message: '', warning: '', error: plainError}
    }

    if (!summary.rosters) {
        return {message: 'Geen roosters gevonden in Shiftbase voor deze periode.', warning: '', error: plainError}
    }

    const skipped = _.groupBy(summary.skipped || [], 'reason')
    const reasons = (Object.keys(skippedReasons) as (keyof typeof skippedReasons)[]).filter((reason) => skipped[reason]?.length)

    const failed: Record<string, ShiftbaseSyncSummary['failed']> = _.groupBy(summary.failed || [], 'message')

    return {
        message: `${count(summary.rosters, 'rooster', 'roosters')} opgehaald uit Shiftbase: ${count(summary.updated, 'dienst', 'diensten')} bijgewerkt, ${count(summary.created, 'dienst', 'diensten')} aangemaakt.`,
        warning: reasons.length > 0 &&
            <ExpandableAlertContent
                title={`Overgeslagen: ${reasons.map((reason) => `${count(skipped[reason].length, 'rooster', 'roosters')} ${skippedReasons[reason].title}`).join(', ')}.`}
                groups={reasons.map((reason) => ({heading: skippedReasons[reason].heading, rosters: skipped[reason].map(({roster}) => roster)}))}
            />,
        error: summary.failed?.length ?
            <ExpandableAlertContent
                title={`${count(summary.failed.length, 'rooster', 'roosters')} niet gesynchroniseerd door een fout${Object.keys(failed).length === 1 ? `: ${Object.keys(failed)[0]}` : ''}.`}
                groups={Object.entries(failed).map(([message, items]) => ({heading: message, rosters: items.map(({roster}) => roster)}))}
            /> :
            plainError
    }
}
